/**
 * The pure derivations behind the registrar's "Ready to lodge with NZPV" card
 * (cca-omhx). No React and no DOM, so the logic that decides WHICH PEOPLE a
 * download and a mark button name — and WHEN the mark is offered — is
 * unit-tested in node (lodgement-card.test.ts) rather than trusted to a render.
 *
 * Two invariants live here. First, at any queue size a pack's Download and its
 * "Mark these N lodged" carry the IDENTICAL id list, and every count shown
 * beside them is the length of that list. Lodgements arrive in gluts; at sixty
 * lodgeable rows the card shows three packs of twenty, never "Mark all 60".
 *
 * Second (registrar ruling 2026-09-17): a pack's mark is offered only once
 * every applicant in that pack has been downloaded IN THIS BROWSER — recorded
 * per applicant in localStorage, so it survives a reload and a re-chunk — or
 * the registrar says he lodged them another way. That is a guard against
 * marking what he never held, not proof of lodgement: a download is not a
 * submission, and the Police application ids pasted back from NZPVS
 * (cca-juiv) are the proof. packReadiness is the one place the rule is
 * decided.
 *
 * The page size is not held here. The queue route publishes `packPageSize`
 * (the worker's LODGEMENT_PACK_PAGE_SIZE) and the card passes it in.
 */

/** The fields of a queue item this module reads. */
export interface LodgementCardItem {
  id: string;
  applicantName: string;
  lodgeable: boolean;
  missingLabels: string[];
  /** Server notes that do NOT block lodging (cca-49qxu) — e.g. more other
   *  names than the official form's rows. Absent from an older server. */
  notes?: string[];
  /** The one note the page draws as a WARNING — the consent is already too old
   *  for NZPVS (cca-uzt5) — or null. Never a block. Absent from an older server. */
  consentWarning?: string | null;
}

/** One pack: a run of lodgeable items, in queue order. */
export interface PackPage<T extends LodgementCardItem = LodgementCardItem> {
  /** 1-based. */
  number: number;
  /** How many packs the lodgeable set makes. */
  of: number;
  items: T[];
  /** The list BOTH the download and the mark send — derived once, here. */
  ids: string[];
  /** Stable identity for per-pack UI state: the ids themselves. */
  key: string;
}

export const PACK_URL = '/api/vetting/registrar/lodgement-queue/pack';

/** How many left-behind people a confirm names before it summarises. */
export const CONFIRM_NAME_LIMIT = 10;

/** A usable page size from the queue response: a positive integer, nothing else. */
export function isPackPageSize(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n > 0;
}

/**
 * Chunk the LODGEABLE items, in queue order, into packs of `pageSize` — the
 * same derivation the worker's pack route runs over the same queue order, so
 * pack N's ids here are page N's rows there. Throws on a page size that is not
 * a positive integer: the card checks isPackPageSize first, and a silent empty
 * result would read as "nothing to lodge".
 */
export function chunkPackPages<T extends LodgementCardItem>(
  items: readonly T[],
  pageSize: number,
): PackPage<T>[] {
  if (!isPackPageSize(pageSize)) {
    throw new RangeError(`pack page size must be a positive integer, got ${String(pageSize)}`);
  }
  const lodgeable = items.filter((i) => i.lodgeable);
  const of = Math.ceil(lodgeable.length / pageSize);
  const pages: PackPage<T>[] = [];
  for (let start = 0; start < lodgeable.length; start += pageSize) {
    const pageItems = lodgeable.slice(start, start + pageSize);
    const ids = pageItems.map((i) => i.id);
    pages.push({ number: pages.length + 1, of, items: pageItems, ids, key: ids.join(',') });
  }
  return pages;
}

/**
 * "Pack 2 of 3 · 20 applicants". No row numbers: "rows 21–40" named numbers
 * that appear nowhere else on the card (Miranda, round 3).
 */
export function packPageLabel(page: Pick<PackPage, 'number' | 'of' | 'items'>): string {
  const n = page.items.length;
  return `Pack ${page.number} of ${page.of} · ${n} ${n === 1 ? 'applicant' : 'applicants'}`;
}

/** The pack URL for exactly these ids; no ids → the unpaged URL (an empty queue). */
export function packHref(ids: readonly string[]): string {
  return ids.length === 0 ? PACK_URL : `${PACK_URL}?ids=${ids.map(encodeURIComponent).join(',')}`;
}

/** The zip's filename from Content-Disposition, or a plain fallback. */
export function packFilenameFrom(contentDisposition: string | null): string {
  const m = /filename="([^"]+)"/.exec(contentDisposition ?? '');
  return m?.[1] ?? 'nzpv-lodgement-pack.zip';
}

/** A pack's mark button — the count IS the pack's id list length. */
export function pageMarkLabel(count: number): string {
  return count === 1 ? 'Mark this 1 lodged' : `Mark these ${count} lodged`;
}

// ── The download record (registrar ruling 2026-09-17) ───────────────────────

/**
 * The localStorage key. Ids and ISO times only — never a name. Exported for
 * LogoutPage too, which removes it at sign-out (Lazo Low 2).
 */
export const DOWNLOAD_RECORD_KEY = 'cca-vetting:lodgement-downloaded';

/** A recorded download older than this no longer counts. */
export const DOWNLOAD_RECORD_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** Applicant id → ISO time of the most recent pack download that held them. */
export type DownloadRecord = ReadonlyMap<string, string>;

/** The slice of Web Storage the record needs — `window.localStorage` in the card. */
export interface DownloadStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The stored JSON back into a record. Anything that is not an object of
 * `id: parseable time` is dropped entry by entry; unreadable JSON is empty.
 */
export function parseDownloadRecord(raw: string | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out;
  for (const [id, at] of Object.entries(parsed)) {
    if (typeof at === 'string' && !Number.isNaN(Date.parse(at))) out.set(id, at);
  }
  return out;
}

/** Both records, keeping the LATER time where an id is in each. */
export function mergeDownloadRecords(a: DownloadRecord, b: DownloadRecord): Map<string, string> {
  const out = new Map(a);
  for (const [id, at] of b) {
    const mine = out.get(id);
    if (mine === undefined || Date.parse(at) > Date.parse(mine)) out.set(id, at);
  }
  return out;
}

/**
 * Keep only ids still in the queue whose download is no older than
 * DOWNLOAD_RECORD_MAX_AGE_MS. Someone who left the queue was lodged or
 * withdrawn; a fortnight-old file is not one to lodge from.
 */
export function pruneDownloadRecord(
  record: DownloadRecord,
  queueIds: Iterable<string>,
  now: Date,
): Map<string, string> {
  const inQueue = new Set(queueIds);
  const cutoff = now.getTime() - DOWNLOAD_RECORD_MAX_AGE_MS;
  const out = new Map<string, string>();
  for (const [id, at] of record) {
    const t = Date.parse(at);
    if (inQueue.has(id) && !Number.isNaN(t) && t >= cutoff) out.set(id, at);
  }
  return out;
}

/** Read the stored record; storage that is missing or throws reads as empty. */
export function readStoredDownloads(store: () => DownloadStore): Map<string, string> {
  try {
    return parseDownloadRecord(store().getItem(DOWNLOAD_RECORD_KEY));
  } catch {
    return new Map();
  }
}

/** Write the record; false when storage is unavailable (the card carries on in-session). */
export function writeStoredDownloads(store: () => DownloadStore, record: DownloadRecord): boolean {
  try {
    store().setItem(DOWNLOAD_RECORD_KEY, JSON.stringify(Object.fromEntries(record)));
    return true;
  } catch {
    return false;
  }
}

/**
 * On a queue load: what storage holds merged with what this session holds,
 * pruned to the queue and the fortnight, and written back pruned.
 */
export function restoreDownloads(opts: {
  store: () => DownloadStore;
  memory: DownloadRecord;
  queueIds: readonly string[];
  now: Date;
}): Map<string, string> {
  const merged = mergeDownloadRecords(readStoredDownloads(opts.store), opts.memory);
  const pruned = pruneDownloadRecord(merged, opts.queueIds, opts.now);
  writeStoredDownloads(opts.store, pruned);
  return pruned;
}

/** After a pack download SUCCEEDS (the blob is saved): every id in it, at `at`. */
export function recordDownloads(opts: {
  store: () => DownloadStore;
  memory: DownloadRecord;
  ids: readonly string[];
  at: Date;
}): Map<string, string> {
  const next = mergeDownloadRecords(readStoredDownloads(opts.store), opts.memory);
  const iso = opts.at.toISOString();
  for (const id of opts.ids) next.set(id, iso);
  writeStoredDownloads(opts.store, next);
  return next;
}

/** Where a pack stands, and what the card may offer for it. */
export interface PackReadiness {
  /**
   * `downloaded` — every id in the pack is recorded. `refused` — not, and the
   * last download of this pack was refused. `not_downloaded` — otherwise.
   */
  status: 'not_downloaded' | 'refused' | 'downloaded';
  /** This pack's download is in flight. */
  downloading: boolean;
  /** The latest recorded time among the pack's ids, when every id is recorded. */
  downloadedAt: string | null;
  /** "Mark these N lodged" is live. */
  canMark: boolean;
  /** "Lodged these another way?" is offered. */
  offerEscape: boolean;
}

/**
 * THE enable rule. Per applicant, not per pack: a re-chunk that moves
 * downloaded people into a new pack keeps it ready, and one new arrival puts
 * their pack back to not-downloaded — they are in no file he holds.
 *
 * LeC's two in-flight holes are closed here: the mark is off while THIS pack
 * is downloading (a "Download again" included), and readiness never reads the
 * error map — so the card clearing an error only on a successful download,
 * never at the start of a retry, cannot re-enable anything early. A failed
 * "Download again" of a pack already downloaded leaves it `downloaded`: the
 * earlier file is still his, and the message is shown beside it.
 */
export function packReadiness(opts: {
  ids: readonly string[];
  key: string;
  downloads: DownloadRecord;
  downloadingKey: string | null;
  errors: Readonly<Record<string, unknown>>;
}): PackReadiness {
  const downloading = opts.downloadingKey === opts.key;
  let latest: string | null = null;
  let every = opts.ids.length > 0;
  for (const id of opts.ids) {
    const at = opts.downloads.get(id);
    if (at === undefined) {
      every = false;
      break;
    }
    if (latest === null || Date.parse(at) > Date.parse(latest)) latest = at;
  }
  const downloadedAt = every ? latest : null;
  const status = downloadedAt
    ? 'downloaded'
    : Object.hasOwn(opts.errors, opts.key)
      ? 'refused'
      : 'not_downloaded';
  return {
    status,
    downloading,
    downloadedAt,
    canMark: status === 'downloaded' && !downloading,
    offerEscape: status !== 'downloaded' && !downloading,
  };
}

const NZ = 'Pacific/Auckland';

/** YYYY-MM-DD on the NZ calendar — for same-day comparison only. */
function nzDay(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * The pill: "Downloaded 10:42am" on the NZ clock (UX-8 — Intl with
 * `timeZone`, never a bare toLocaleTimeString). A download from an earlier NZ
 * day says which day, so a week-old pill does not read as this morning's.
 */
export function downloadedLabel(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'Downloaded';
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: NZ,
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const time = `${get('hour')}:${get('minute')}${get('dayPeriod').toLowerCase()}`;
  return nzDay(at) === nzDay(now)
    ? `Downloaded ${time}`
    : `Downloaded ${get('day')} ${get('month')}, ${time}`;
}

// ── Confirm and messages ────────────────────────────────────────────────────

/** What the bulk confirm is built from. */
export interface LodgeConfirmOptions<T extends LodgementCardItem = LodgementCardItem> {
  marking: readonly T[];
  left: readonly T[];
  page?: PackPage<T>;
  /** All lodgeable rows on the card — only read with `page`. */
  lodgeableTotal?: number;
  /** The "Lodged these another way?" path: no recorded download. */
  unrecorded?: boolean;
}

/**
 * The bulk confirm (cca-djp1, round 6). It asks the question that matters — were
 * these submitted in NZPVS? — because a download is not a submission. It
 * states the set that will be marked, names who is being left behind, and, on
 * the "lodged another way" path, says no pack was downloaded in this browser.
 * At a glut the left-behind list is capped at CONFIRM_NAME_LIMIT and the rest
 * counted. For one of several packs it names the pack and says the others stay.
 */
export function lodgeConfirmText<T extends LodgementCardItem>(
  opts: LodgeConfirmOptions<T>,
): string {
  const { marking, left, page } = opts;
  const n = marking.length;
  const noun = n === 1 ? 'application' : 'applications';
  const verb = n === 1 ? 'Has' : 'Have';
  const lines = [
    page
      ? `${verb} the ${n} ${noun} in Pack ${page.number} of ${page.of} been submitted in NZPVS?`
      : `${verb} ${n === 1 ? 'this' : 'these'} ${n} ${noun} been submitted in NZPVS?`,
    `OK marks ${n === 1 ? 'it' : 'them'} lodged with NZPV. A downloaded pack is not a submission.`,
  ];
  if (opts.unrecorded) lines.push('', 'No pack was downloaded in this browser.');
  const otherPacks = page ? (opts.lodgeableTotal ?? n) - n : 0;
  if (otherPacks > 0) {
    lines.push(
      '',
      `The other ${otherPacks} lodgeable ${otherPacks === 1 ? 'application stays' : 'applications stay'} in the queue for ${otherPacks === 1 ? 'its' : 'their'} own pack.`,
    );
  }
  if (left.length > 0) {
    const named = left
      .slice(0, CONFIRM_NAME_LIMIT)
      .map((i) => `• ${i.applicantName} — missing ${i.missingLabels.join(', ')}`);
    const more = left.length - named.length;
    lines.push(
      '',
      `${left.length} ${left.length === 1 ? 'person is' : 'people are'} NOT lodgeable and will be left in the queue — they were never sent to Police:`,
      ...named,
      ...(more > 0 ? [`…and ${more} more.`] : []),
    );
  }
  return lines.join('\n');
}

/** Which of a pack's two mark controls was pressed. */
export type PackMarkControl = 'mark' | 'escape';

/**
 * The confirm arguments for a pack's mark (cca-omhx round 7, LeC/Tyson g2).
 * Both controls confirm the SAME set — the pack's items, the ids its download
 * carries — and differ only in words: `escape` ("Lodged these another way?")
 * requests the unrecorded confirm, the one that says no pack was downloaded in
 * this browser; `mark` does not. The card calls this for both buttons.
 */
export function packMarkConfirmOptions<T extends LodgementCardItem>(
  control: PackMarkControl,
  opts: { page: PackPage<T>; left: readonly T[]; paged: boolean; lodgeableTotal: number },
): LodgeConfirmOptions<T> {
  return {
    marking: opts.page.items,
    left: opts.left,
    ...(opts.paged ? { page: opts.page, lodgeableTotal: opts.lodgeableTotal } : {}),
    ...(control === 'escape' ? { unrecorded: true } : {}),
  };
}

/**
 * The per-row confirm (Miranda S1, round 7). A row's "Mark lodged" is as
 * irreversible from this page as a pack's, so it asks the same question of
 * one named person before anything is written.
 */
export function rowLodgeConfirmText(item: Pick<LodgementCardItem, 'applicantName'>): string {
  return [
    `Has the application for ${item.applicantName} been submitted in NZPVS?`,
    'OK marks it lodged with NZPV. A downloaded pack is not a submission.',
  ].join('\n');
}

/**
 * A row's non-blocking notes as the lines the card shows under it
 * (cca-49qxu): one line per note, each "Note: …" (Miranda r1 M-9), and an
 * empty list when there are none. A note never touches `lodgeable`, the packs
 * or the mark: it is something to know, not something to fix.
 */
export function rowNotesText(item: Pick<LodgementCardItem, 'notes' | 'consentWarning'>): string[] {
  // The consent warning is drawn as a warning (rowConsentWarning), never twice.
  const warning = rowConsentWarning(item);
  return (item.notes ?? [])
    .filter((n) => n.trim() !== '' && n !== warning)
    .map((n) => `Note: ${n}`);
}

/**
 * The row's consent warning (cca-uzt5, Miranda M-S2): the server's
 * `consentWarning` when it is one of the row's own notes — picked out by
 * equality, never by parsing the sentence — else null. Like every note it
 * blocks nothing: Copy and Mark lodged stay (registrar, 29 Oct 2026).
 */
export function rowConsentWarning(
  item: Pick<LodgementCardItem, 'notes' | 'consentWarning'>,
): string | null {
  const w = item.consentWarning;
  return typeof w === 'string' && w.trim() !== '' && (item.notes ?? []).includes(w) ? w : null;
}

/**
 * The pack mark button's disabled rule (e2): off unless packReadiness offers
 * the mark, and off while ANY mark is in flight — a row's or another pack's.
 */
export function markDisabled(ready: Pick<PackReadiness, 'canMark'>, busy: string | null): boolean {
  return busy !== null || !ready.canMark;
}

/**
 * A row's own mark is off for a row that is not lodgeable, and off while ANY
 * mark is in flight (Miranda S1d): a row click during a pack mark used to
 * overwrite the busy key, so the pack button left "Marking…" early.
 */
export function rowMarkDisabled(
  item: Pick<LodgementCardItem, 'lodgeable'>,
  busy: string | null,
): boolean {
  return busy !== null || !item.lodgeable;
}

/**
 * What the pack's status region says once its download is saved (Miranda S2,
 * WCAG 2.2 SC 4.1.3): the file is here, and a download is not a lodgement.
 */
export function packDownloadedAnnouncement(packNumber: number): string {
  return `Pack ${packNumber} downloaded. Mark them once they are submitted in NZPVS.`;
}

/** A step in one pack's download, as the error map sees it. */
export type DownloadOutcome<F> =
  { kind: 'started' } | { kind: 'saved' } | { kind: 'refused'; failure: F };

/**
 * The pack error map after a download step (f): a refusal is set, a saved
 * download clears it, and a retry STARTING leaves it exactly as it was — the
 * message stays beside the button until the retry has an answer (LeC). The
 * map is returned unchanged (the same object) when nothing moves.
 */
export function packErrorsAfter<F>(
  errors: Record<string, F>,
  key: string,
  outcome: DownloadOutcome<F>,
): Record<string, F> {
  if (outcome.kind === 'refused') return { ...errors, [key]: outcome.failure };
  if (outcome.kind === 'saved' && Object.hasOwn(errors, key)) {
    return Object.fromEntries(Object.entries(errors).filter(([k]) => k !== key));
  }
  return errors;
}

/** Plain words for a pack failure reason (the worker's LodgementPackFailureReason). */
export function packFailureReasonText(reason: string): string {
  switch (reason) {
    case 'not_found':
      return 'consent PDF not found in Drive';
    case 'not_pdf':
      return 'the consent file is not a PDF';
    case 'invalid_id':
      return 'the record id is not a Zoho id';
    case 'over_budget':
      return "this pack's PDFs are too large to bundle together";
    case 'no_drive_file':
      return 'no Drive consent file is linked';
    case 'timeout':
      return 'Drive took too long';
    case 'network':
    case 'no_token':
      return 'Drive could not be reached';
  }
  const http = /^http_(\d{3})$/.exec(reason);
  if (http) {
    const status = Number(http[1]);
    return status >= 500 || status === 429 || status === 408
      ? 'Drive was unavailable'
      : `Drive refused the file (HTTP ${status})`;
  }
  return reason;
}

/** One person in a refusal list. */
export interface PackFailureLine {
  id: string;
  name: string;
  reason: string;
}

/**
 * What the card says, in place, when a pack did not download — as lists, not
 * a paragraph (Miranda, round 3). The worker never ships a partial pack, so
 * the headline is always "did not download"; each person is listed under
 * whether trying again can help — the server's `retryable` flag, not a guess.
 */
export interface PackFailureView {
  /** "Pack 2 did not download." */
  headline: string;
  /** Under RETRY_LIST_HEADING. */
  retry: PackFailureLine[];
  /** Under STUCK_LIST_HEADING. */
  stuck: PackFailureLine[];
  /** A sentence for failures that are not about particular people. */
  note: string | null;
}

export const RETRY_LIST_HEADING = 'Try again in a minute:';
export const STUCK_LIST_HEADING = "Fix these first — trying again won't help:";

interface PackFailureBody {
  error?: unknown;
  failures?: unknown;
  refused?: unknown;
  unknown?: unknown;
}

function stringArray(x: unknown): string[] {
  return Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : [];
}

function failureHeadline(packNumber: number | null): string {
  return packNumber === null
    ? 'The pack did not download.'
    : `Pack ${packNumber} did not download.`;
}

/**
 * The refusal, from the pack route's status and body. `packNumber` is null
 * when the queue makes one pack (the headline then says "The pack").
 */
export function packFailureView(
  status: number,
  body: unknown,
  nameOf: (id: string) => string,
  packNumber: number | null,
): PackFailureView {
  const view: PackFailureView = {
    headline: failureHeadline(packNumber),
    retry: [],
    stuck: [],
    note: null,
  };
  const b = (body && typeof body === 'object' ? body : {}) as PackFailureBody;
  if (b.error === 'pack_incomplete' && Array.isArray(b.failures)) {
    for (const f of b.failures as unknown[]) {
      if (!f || typeof f !== 'object') continue;
      const { recordId, reason, retryable } = f as Record<string, unknown>;
      if (typeof recordId !== 'string' || typeof reason !== 'string') continue;
      const line = { id: recordId, name: nameOf(recordId), reason: packFailureReasonText(reason) };
      (retryable === true ? view.retry : view.stuck).push(line);
    }
    if (view.retry.length === 0 && view.stuck.length === 0) {
      view.note = `Something went wrong (HTTP ${status}). Try again in a minute.`;
    }
    return view;
  }
  if (b.error === 'not_lodgeable') {
    const names = [...stringArray(b.refused), ...stringArray(b.unknown)].map(nameOf);
    view.note = `The queue changed while this page was open${
      names.length > 0 ? ` (${names.join(', ')})` : ''
    }. Reload the page and try again.`;
    return view;
  }
  if (b.error === 'page_required' || b.error === 'page_too_large' || b.error === 'invalid_ids') {
    view.note = 'The queue no longer matches this page. Reload the page and try again.';
    return view;
  }
  if (status === 401 || status === 403) {
    view.note = 'Sign in again as the registrar, then try again.';
    return view;
  }
  view.note = `Something went wrong (HTTP ${status}). Try again in a minute.`;
  return view;
}

/** The refusal when the request never got a response. */
export function packNetworkFailure(packNumber: number | null): PackFailureView {
  return {
    headline: failureHeadline(packNumber),
    retry: [],
    stuck: [],
    note: 'Check the connection and try again.',
  };
}

/** lodge-all's 200 body, as the card reads it. */
export interface LodgeAllResult {
  lodged: number;
  failed: { id: string; name: string; retryable?: boolean }[];
}

/**
 * The message after a bulk mark, or null when every row was lodged. Failed
 * rows are still in the queue; the server says which a retry can help (a Zoho
 * rate limit or outage) and which it cannot. A missing flag (an older worker)
 * is treated as retryable — retrying a lodge is harmless.
 */
export function lodgeResultMessage(res: LodgeAllResult): string | null {
  if (res.failed.length === 0) return null;
  const again = res.failed.filter((f) => f.retryable !== false).map((f) => f.name);
  const stuck = res.failed.filter((f) => f.retryable === false).map((f) => f.name);
  const parts = [`${res.lodged} lodged; ${res.failed.length} not lodged and still in the queue.`];
  if (again.length > 0)
    parts.push(`Zoho was busy or unreachable — try again for: ${again.join(', ')}.`);
  if (stuck.length > 0) {
    parts.push(`Trying again will not help for: ${stuck.join(', ')} — open the record to see why.`);
  }
  return parts.join(' ');
}
